import * as React from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Link from "next/link";
import { motion } from "framer-motion";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";
import { GetAlbumDetailsQuery } from "@src/@types/api.d";

type Props = {
  eventId: number;
  albumDetailsData: GetAlbumDetailsQuery;
  eventSlug: string;
};

export function insertAt(
  array: unknown[],
  index: number,
  ...elementsArray: unknown[]
) {
  array.splice(index, 0, ...elementsArray);
}

const Masonry: React.FC<Props> = ({ eventId, eventSlug, albumDetailsData }) => {
  const mediaData = React.useMemo(
    () => albumDetailsData?.getMedia || [],
    [albumDetailsData]
  );

  const imageMedia = React.useMemo(
    () =>
      (mediaData || []).filter(
        (media) =>
          media &&
          typeof media?.type === "string" &&
          media.type.toLowerCase().includes("image") &&
          media?.meta?.s3_url?.startsWith("http")
      ),
    [mediaData]
  );

  if (imageMedia.length === 0) return null;

  const minCount = 12;
  const repeatTimes = Math.ceil(minCount / imageMedia.length);
  const loopImages = Array.from(
    { length: repeatTimes },
    () => imageMedia
  ).flat();

  const itemWidth = 356; // 340px image + 16px gap
  const trackWidth = loopImages.length * itemWidth;
  const duration = loopImages.length * 3;

  return (
    <div className="min-h-[calc(100vh-var(--t22-header-height))] w-full py-14 overflow-hidden relative bg-[#1C1820] flex flex-col">
      <div className="mb-12 text-center px-6 md:mb-16">
        <span className="mb-4 block font-jost text-[0.62rem] font-medium uppercase tracking-[0.35em] text-[#228B22]">
          Gallery
        </span>
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-corGaramond text-[clamp(3rem,7vw,5.4rem)] font-normal leading-[1.02] text-[#401d15]"
        >
          Snapshots{" "}
          <em className="font-light italic text-[#228B22]">of Forever</em>
        </motion.h2>
        <div className="mt-5 flex items-center justify-center gap-4">
          <span className="h-px w-[56px] bg-gradient-to-r from-transparent to-[#228B22]" />
          <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
          <span className="h-px w-[56px] bg-gradient-to-r from-[#228B22] to-transparent" />
        </div>
      </div>
      {/* Auto-scrolling strip */}
      <div className="w-full overflow-hidden mb-10">
        <div
          className="flex will-change-transform"
          style={{
            gap: "16px",
            width: `${trackWidth * 2}px`,
            animation: `masonry-scroll ${duration}s linear infinite`,
          }}
        >
          {/* First copy */}
          {loopImages.map((img, idx) => (
            <div
              key={`a-${idx}`}
              className="flex-shrink-0 w-[340px] h-[470px] md:h-[410px] overflow-hidden relative"
            >
              <Image
                src={img?.meta?.s3_url || flowerBackground}
                layout="fill"
                objectFit="cover"
                alt={`Gallery image ${idx + 1}`}
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(340, 280)
                )}`}
                onError={(event) => {
                  const target = event.currentTarget as HTMLImageElement;
                  if (!target.src.includes("bw-flower.jpeg")) {
                    target.src = flowerBackground.src;
                  }
                }}
              />
            </div>
          ))}
          {/* Second copy for seamless loop */}
          {loopImages.map((img, idx) => (
            <div
              key={`b-${idx}`}
              className="flex-shrink-0 w-[340px] h-[240px] md:h-[280px] overflow-hidden relative"
            >
              <Image
                src={img?.meta?.s3_url || flowerBackground}
                layout="fill"
                objectFit="cover"
                alt={`Gallery image ${idx + 1}`}
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(340, 280)
                )}`}
                onError={(event) => {
                  const target = event.currentTarget as HTMLImageElement;
                  if (!target.src.includes("bw-flower.jpeg")) {
                    target.src = flowerBackground.src;
                  }
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Button */}
      {eventSlug && (
        <div className="flex justify-center">
          <Link
            href={`https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`}
            passHref
          >
            <a target="_blank" rel="noopener noreferrer">
              <button
                className="border border-[#401d15] px-8 py-3 font-jost text-[0.58rem] font-normal uppercase tracking-[0.22em] text-[#401d15] transition hover:bg-[#401d15] hover:text-white"
                type="button"
              >
                View Full Photobook
              </button>
            </a>
          </Link>
        </div>
      )}

      <style>{`
        @keyframes masonry-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-${trackWidth}px); }
        }
      `}</style>
    </div>
  );
};

export default Masonry;
